<!-- app/javascript/dashboard/modules/tasks/components/TaskForm.vue -->
<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';
import KanbanAPI from 'dashboard/api/kanban';
import TaskDatePicker from './TaskDatePicker.vue';
import SmartTitleInput from './SmartTitleInput.vue';
import { buildDueIso, recurrenceEndsToIso } from '../helpers/dueDate';

const props = defineProps({
  prefill: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['created', 'cancel']);

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');
const labels = useMapGetter('labels/getLabels');

function todayDateString() {
  const today = new Date();
  const offset = today.getTimezoneOffset() * 60000;
  return new Date(today.getTime() - offset).toISOString().slice(0, 10);
}

const title = ref('');
const description = ref('');
const priority = ref('no_priority');
const datePickerValue = ref({
  due_date: props.prefill.due_date ?? todayDateString(),
  recurrence_type: null,
  recurrence_interval: null,
  recurrence_unit: null,
  recurrence_ends_at: null,
  recurrence_count: null,
});
const showDatePicker = ref(false);
const taskType = ref('');
const assigneeId = ref(
  props.prefill.assignee_id ?? currentUser.value?.id ?? null
);
const selectedLabels = ref([]);
const isSubmitting = ref(false);
const error = ref('');

const contactSearch = ref('');
const contactResults = ref([]);
const selectedContact = ref(null);
const showContactDropdown = ref(false);
const contactHighlightedIndex = ref(0);
const showAssigneeDropdown = ref(false);
const showLabelsDropdown = ref(false);
const showTypeDropdown = ref(false);
const showPriorityDropdown = ref(false);

const isResolvingLeader = ref(false);

async function resolveLeaderInForm() {
  const contactId = selectedContact.value?.id ?? null;
  if (!contactId) return;
  isResolvingLeader.value = true;
  showAssigneeDropdown.value = false;
  try {
    try {
      const { data } = await KanbanAPI.getCardsByContact(contactId);
      const cards = data?.data ?? [];
      if (cards.length) {
        const latest = cards.reduce((a, b) => (a.id > b.id ? a : b));
        const agentId = latest.assignee?.id ?? latest.assignee_id ?? null;
        if (agentId) {
          assigneeId.value = agentId;
          return;
        }
      }
    } catch {
      // no-op
    }

    try {
      const { data } = await ContactAPI.getConversations(contactId);
      const conversations = data?.payload ?? [];
      if (conversations.length) {
        const last = conversations.reduce((a, b) => (a.id > b.id ? a : b));
        const agentId = last.meta?.assignee?.id ?? null;
        if (agentId) {
          assigneeId.value = agentId;
        }
      }
    } catch {
      // no-op
    }
  } finally {
    isResolvingLeader.value = false;
  }
}

function onSmartPendingFields(fields) {
  if (fields.description !== null) description.value = fields.description;
  if (fields.priority) priority.value = fields.priority;
  if (fields.assignee_id) assigneeId.value = fields.assignee_id;
  if (fields.task_type) taskType.value = fields.task_type;
  if (fields.label_list?.length) selectedLabels.value = fields.label_list;
  if (fields.due_date) {
    datePickerValue.value = {
      ...datePickerValue.value,
      due_date: fields.due_date,
      due_time: fields.due_time ?? datePickerValue.value.due_time,
    };
  }
  if (fields.contact_id && !selectedContact.value) {
    selectedContact.value = { id: fields.contact_id, name: '' };
    contactSearch.value = '';
  }
  if (fields.recurrence_type) {
    datePickerValue.value = {
      ...datePickerValue.value,
      recurrence_type: fields.recurrence_type,
      recurrence_interval: fields.recurrence_interval ?? null,
      recurrence_unit: fields.recurrence_unit ?? null,
    };
  }
}

const TASK_TYPES = [
  { key: 'general', label: t('TASKS.TYPES.GENERAL') },
  { key: 'call', label: t('TASKS.TYPES.CALL') },
  { key: 'email', label: t('TASKS.TYPES.EMAIL') },
  { key: 'meeting', label: t('TASKS.TYPES.MEETING') },
  { key: 'follow_up', label: t('TASKS.TYPES.FOLLOW_UP') },
];

const PRIORITY_OPTIONS = [
  { key: 'urgent', label: t('TASKS.PRIORITY.URGENT'), dot: 'text-n-ruby-9' },
  { key: 'high', label: t('TASKS.PRIORITY.HIGH'), dot: 'text-n-amber-9' },
  { key: 'normal', label: t('TASKS.PRIORITY.NORMAL'), dot: 'text-n-blue-9' },
  {
    key: 'no_priority',
    label: t('TASKS.PRIORITY.NONE'),
    dot: 'text-n-slate-8',
  },
];

const TYPE_ICONS = {
  general: 'i-lucide-check-square',
  call: 'i-lucide-phone',
  email: 'i-lucide-mail',
  meeting: 'i-lucide-calendar',
  follow_up: 'i-lucide-refresh-cw',
};

const selectedAssignee = computed(
  () => agents.value?.find(a => a.id === assigneeId.value) ?? null
);
const selectedPriority = computed(
  () =>
    PRIORITY_OPTIONS.find(o => o.key === priority.value) ?? PRIORITY_OPTIONS[0]
);
const selectedType = computed(
  () => TASK_TYPES.find(o => o.key === taskType.value) ?? null
);

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function normalizeContact(c) {
  return {
    ...c,
    company_name:
      c.company_name ?? c.additional_attributes?.company_name ?? null,
  };
}

async function searchContacts(query) {
  if (!query || query.length < 1) {
    contactResults.value = [];
    showContactDropdown.value = false;
    return;
  }
  try {
    const { data } = await ContactAPI.search(query, 1, 'name');
    contactResults.value = (data?.payload || []).map(normalizeContact);
    contactHighlightedIndex.value = 0;
    showContactDropdown.value = contactResults.value.length > 0;
  } catch {
    contactResults.value = [];
    showContactDropdown.value = false;
  }
}

function selectContact(contact) {
  selectedContact.value = contact;
  contactSearch.value = contact.name;
  showContactDropdown.value = false;
  contactResults.value = [];
}

function onContactKeydown(e) {
  if (!showContactDropdown.value) return;
  if (e.key === 'Escape') {
    showContactDropdown.value = false;
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.min(
      contactHighlightedIndex.value + 1,
      contactResults.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.max(
      contactHighlightedIndex.value - 1,
      0
    );
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = contactResults.value[contactHighlightedIndex.value];
    if (item) selectContact(item);
  }
}

onMounted(() => {
  window.addEventListener('keydown', onContactKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onContactKeydown);
});

function toggleLabel(label) {
  const idx = selectedLabels.value.indexOf(label.title);
  if (idx === -1) selectedLabels.value.push(label.title);
  else selectedLabels.value.splice(idx, 1);
}

function closeAllDropdowns() {
  showContactDropdown.value = false;
  showAssigneeDropdown.value = false;
  showLabelsDropdown.value = false;
  showTypeDropdown.value = false;
  showPriorityDropdown.value = false;
}

function toggleDropdown(name) {
  const map = {
    assignee: showAssigneeDropdown,
    labels: showLabelsDropdown,
    type: showTypeDropdown,
    priority: showPriorityDropdown,
  };
  const current = map[name].value;
  closeAllDropdowns();
  map[name].value = !current;
}

async function submit() {
  if (!title.value.trim()) {
    error.value = t('TASKS.ERRORS.TITLE_REQUIRED');
    return;
  }
  isSubmitting.value = true;
  error.value = '';
  const payload = {
    title: title.value.trim(),
    description: description.value.trim() || null,
    priority: priority.value,
    due_date: buildDueIso(
      datePickerValue.value.due_date,
      datePickerValue.value.due_time
    ),
    task_type: taskType.value || null,
    assignee_id: assigneeId.value || null,
    label_list: selectedLabels.value.length ? selectedLabels.value : undefined,
    recurrence_type: datePickerValue.value.recurrence_type || null,
    recurrence_interval: datePickerValue.value.recurrence_interval || null,
    recurrence_unit: datePickerValue.value.recurrence_unit || null,
    recurrence_ends_at: recurrenceEndsToIso(
      datePickerValue.value.recurrence_ends_at
    ),
    recurrence_count: datePickerValue.value.recurrence_count || null,
    recurrence_occurrences: 0,
    ...props.prefill,
  };
  if (selectedContact.value && !props.prefill.contact_id) {
    payload.contact_id = selectedContact.value.id;
  }
  const result = await store.dispatch('tasks/createTask', payload);
  isSubmitting.value = false;
  if (result.success) {
    emit('created', result.data);
  } else {
    error.value = t('TASKS.ERRORS.CREATE');
  }
}
</script>

<template>
  <div
    class="flex flex-col bg-n-background border border-n-weak rounded-xl shadow-xl w-full md:w-[480px]"
    @click.stop="closeAllDropdowns"
  >
    <!-- Título -->
    <div class="px-4 pt-4">
      <SmartTitleInput
        v-model="title"
        :placeholder="t('TASKS.FORM.TITLE_PLACEHOLDER')"
        focus-at-end
        class="w-full text-base font-medium text-n-slate-12"
        @update:pending-fields="onSmartPendingFields"
        @submit="submit"
        @cancel="emit('cancel')"
      />
    </div>

    <!-- Descrição -->
    <div class="px-4 pt-2 pb-3">
      <textarea
        v-model="description"
        :placeholder="t('TASKS.FORM.DESCRIPTION_PLACEHOLDER')"
        class="w-full text-sm text-n-slate-12 bg-transparent border-0 outline-none focus:ring-0 resize-none placeholder:text-n-slate-10"
        rows="2"
      />
    </div>

    <!-- Divisor -->
    <div class="border-t border-n-weak" />

    <!-- Grid de campos -->
    <div
      class="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-n-weak"
    >
      <!-- Coluna esquerda: Contato, Tipo, Tags -->
      <div class="flex flex-col divide-y divide-n-weak">
        <!-- Contato -->
        <div
          v-if="!prefill.contact_id"
          class="relative px-3 py-2.5"
          @click.stop
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-user size-3.5 text-n-slate-9 flex-shrink-0" />
            <input
              v-model="contactSearch"
              type="text"
              :placeholder="t('TASKS.FORM.CONTACT_PLACEHOLDER')"
              class="w-full text-sm text-n-slate-12 bg-transparent border-0 outline-none focus:outline-none focus:ring-0 placeholder:text-n-slate-10 min-w-0"
              autocomplete="off"
              @input="searchContacts($event.target.value)"
            />
          </div>
          <div
            v-if="showContactDropdown && contactResults.length"
            class="absolute z-30 left-0 right-0 top-full mt-1 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 max-h-48 overflow-y-auto"
            @click.stop
          >
            <button
              v-for="(c, idx) in contactResults"
              :key="c.id"
              type="button"
              class="w-full text-left px-3 py-2 text-sm transition-colors"
              :class="
                idx === contactHighlightedIndex
                  ? 'text-n-brand font-medium bg-n-alpha-1'
                  : 'text-n-slate-12 hover:bg-n-alpha-1'
              "
              @mousedown.prevent="selectContact(c)"
            >
              {{ c.name
              }}<span
                v-if="c.company_name"
                class="text-n-slate-10 text-xs ml-1"
              >
                · {{ c.company_name }}
              </span>
            </button>
          </div>
        </div>

        <!-- Tipo -->
        <div class="relative px-3 py-2.5" @click.stop>
          <button
            type="button"
            class="flex items-center gap-2 w-full text-left"
            @click="toggleDropdown('type')"
          >
            <span
              class="size-3.5 flex-shrink-0 text-n-slate-9"
              :class="
                selectedType
                  ? TYPE_ICONS[selectedType.key]
                  : 'i-lucide-layout-list'
              "
            />
            <span
              class="text-sm"
              :class="selectedType ? 'text-n-slate-12' : 'text-n-slate-10'"
            >
              {{
                selectedType ? selectedType.label : t('TASKS.FILTERS.TASK_TYPE')
              }}
            </span>
          </button>
          <div
            v-if="showTypeDropdown"
            class="absolute z-20 left-0 right-0 mt-1 bg-n-background border border-n-weak rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto"
          >
            <button
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-10 hover:bg-n-alpha-1"
              @click="
                taskType = '';
                showTypeDropdown = false;
              "
            >
              <span class="i-lucide-x size-3.5 text-n-slate-9" />
              {{ t('TASKS.PRIORITY.NONE') }}
            </button>
            <div class="border-t border-n-weak" />
            <button
              v-for="opt in TASK_TYPES"
              :key="opt.key"
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1"
              :class="
                taskType === opt.key
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
              "
              @click="
                taskType = opt.key;
                showTypeDropdown = false;
              "
            >
              <span
                class="size-3.5 flex-shrink-0"
                :class="TYPE_ICONS[opt.key]"
              />
              {{ opt.label }}
            </button>
          </div>
        </div>

        <!-- Tags -->
        <div class="relative px-3 py-2.5" @click.stop>
          <button
            type="button"
            class="flex items-center gap-2 w-full text-left flex-wrap"
            @click="toggleDropdown('labels')"
          >
            <span class="i-lucide-tag size-3.5 text-n-slate-9 flex-shrink-0" />
            <template v-if="selectedLabels.length">
              <span
                v-for="lbl in selectedLabels"
                :key="lbl"
                class="text-[11px] font-medium px-1.5 py-px rounded-full bg-n-blue-2 text-n-blue-11"
              >
                {{ lbl }}
              </span>
            </template>
            <span v-else class="text-sm text-n-slate-10">
              {{ t('TASKS.FIELDS.LABELS') }}
            </span>
          </button>
          <div
            v-if="showLabelsDropdown"
            class="absolute z-20 left-0 right-0 mt-1 bg-n-background border border-n-weak rounded-lg shadow-lg py-1 max-h-44 overflow-y-auto"
          >
            <p v-if="!labels?.length" class="px-3 py-2 text-sm text-n-slate-10">
              {{ t('TASKS.TAGS.EMPTY') }}
            </p>
            <button
              v-for="label in labels"
              :key="label.id"
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1"
              :class="
                selectedLabels.includes(label.title)
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
              "
              @click.stop="toggleLabel(label)"
            >
              <span
                class="size-2.5 rounded-full flex-shrink-0"
                :style="{ background: label.color }"
              />
              <span class="truncate flex-1">{{ label.title }}</span>
              <span
                v-if="selectedLabels.includes(label.title)"
                class="i-lucide-check size-3.5 text-n-brand"
              />
            </button>
          </div>
        </div>
      </div>

      <!-- Coluna direita: Responsável, Prioridade, Prazo -->
      <div class="flex flex-col divide-y divide-n-weak">
        <!-- Responsável -->
        <div class="relative px-3 py-2.5" @click.stop>
          <button
            type="button"
            class="flex items-center gap-2 w-full text-left"
            @click="toggleDropdown('assignee')"
          >
            <div
              v-if="selectedAssignee"
              class="size-5 rounded-full bg-n-slate-3 overflow-hidden flex items-center justify-center flex-shrink-0 border border-n-weak"
            >
              <img
                v-if="selectedAssignee.thumbnail"
                :src="selectedAssignee.thumbnail"
                class="size-full object-cover"
              />
              <!-- eslint-disable-next-line prettier/prettier -->
              <span v-else class="text-[9px] font-bold text-n-slate-11">{{ initials(selectedAssignee.name) }}</span>
            </div>
            <span
              v-else
              class="i-lucide-user-plus size-3.5 text-n-slate-9 flex-shrink-0"
            />
            <span
              class="text-sm truncate"
              :class="selectedAssignee ? 'text-n-slate-12' : 'text-n-slate-10'"
            >
              {{
                selectedAssignee
                  ? selectedAssignee.name
                  : t('TASKS.FIELDS.ASSIGNEE')
              }}
            </span>
          </button>
          <div
            v-if="showAssigneeDropdown"
            class="absolute z-20 left-0 right-0 mt-1 bg-n-background border border-n-weak rounded-lg shadow-lg py-1 max-h-44 overflow-y-auto"
          >
            <button
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1"
              :disabled="isResolvingLeader || !selectedContact"
              @click="resolveLeaderInForm"
            >
              <span
                class="size-3.5 flex-shrink-0"
                :class="
                  isResolvingLeader
                    ? 'i-lucide-loader animate-spin text-n-slate-9'
                    : 'i-lucide-crown text-n-amber-9'
                "
              />
              <span :class="isResolvingLeader ? 'text-n-slate-9' : ''">
                {{
                  isResolvingLeader
                    ? $t('KANBAN.TASK_GROUPS.LEADER.RESOLVING')
                    : $t('KANBAN.TASK_GROUPS.LEADER.BUTTON')
                }}
              </span>
            </button>
            <div class="border-t border-n-weak my-1" />
            <button
              v-for="agent in agents"
              :key="agent.id"
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1"
              :class="
                agent.id === assigneeId
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
              "
              @click="
                assigneeId = agent.id;
                showAssigneeDropdown = false;
              "
            >
              <div
                class="size-5 rounded-full bg-n-slate-3 overflow-hidden flex items-center justify-center flex-shrink-0 border border-n-weak"
              >
                <img
                  v-if="agent.thumbnail"
                  :src="agent.thumbnail"
                  class="size-full object-cover"
                />
                <!-- eslint-disable-next-line prettier/prettier -->
                <span v-else class="text-[9px] font-bold text-n-slate-11">{{ initials(agent.name) }}</span>
              </div>
              <span class="truncate">{{ agent.name }}</span>
            </button>
            <div class="border-t border-n-weak my-1" />
            <button
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1"
              @click="
                assigneeId = null;
                showAssigneeDropdown = false;
              "
            >
              <span class="i-lucide-user-x size-3.5 text-n-slate-9" />
              {{ t('TASKS.MODAL.UNASSIGN') }}
            </button>
          </div>
        </div>

        <!-- Prioridade -->
        <div class="relative px-3 py-2.5" @click.stop>
          <button
            type="button"
            class="flex items-center gap-2 w-full text-left"
            @click="toggleDropdown('priority')"
          >
            <span
              v-if="priority !== 'no_priority'"
              class="i-lucide-flag size-3.5 flex-shrink-0"
              :class="selectedPriority.dot"
            />
            <span
              v-else
              class="i-lucide-flag size-3.5 flex-shrink-0 text-n-slate-8"
            />
            <span
              class="text-sm"
              :class="
                priority === 'no_priority'
                  ? 'text-n-slate-10'
                  : 'text-n-slate-12'
              "
            >
              {{ selectedPriority.label }}
            </span>
          </button>
          <div
            v-if="showPriorityDropdown"
            class="absolute z-20 left-0 right-0 mt-1 bg-n-background border border-n-weak rounded-lg shadow-lg py-1"
          >
            <button
              v-for="opt in PRIORITY_OPTIONS"
              :key="opt.key"
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1"
              :class="
                priority === opt.key
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
              "
              @click="
                priority = opt.key;
                showPriorityDropdown = false;
              "
            >
              <span
                class="i-lucide-flag size-3 flex-shrink-0"
                :class="opt.dot"
              />
              {{ opt.label }}
            </button>
          </div>
        </div>

        <!-- Prazo -->
        <div class="relative px-3 py-2.5" @click.stop>
          <button
            type="button"
            class="flex items-center gap-2 w-full text-left"
            @click="showDatePicker = !showDatePicker"
          >
            <span
              class="i-lucide-calendar size-3.5 text-n-slate-9 flex-shrink-0"
            />
            <span
              class="text-sm"
              :class="
                datePickerValue.due_date ? 'text-n-slate-12' : 'text-n-slate-10'
              "
            >
              {{
                datePickerValue.due_date
                  ? new Date(datePickerValue.due_date).toLocaleDateString(
                      'pt-BR',
                      {
                        day: 'numeric',
                        month: 'short',
                      }
                    )
                  : t('TASKS.FIELDS.DUE_DATE')
              }}
            </span>
            <span
              v-if="datePickerValue.recurrence_type"
              class="i-lucide-repeat size-3 text-n-slate-9 ml-auto"
            />
          </button>
          <div v-if="showDatePicker" class="absolute left-0 top-10 z-30">
            <TaskDatePicker
              v-model="datePickerValue"
              @update:model-value="showDatePicker = false"
              @clear-time="() => {}"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Divisor -->
    <div class="border-t border-n-weak" />

    <!-- Erro -->
    <p v-if="error" class="px-4 pt-3 text-xs text-n-ruby-11">{{ error }}</p>

    <!-- Rodapé -->
    <div class="flex justify-end gap-2 px-4 py-3">
      <button
        class="px-3 py-1.5 text-sm text-n-slate-11 hover:text-n-slate-12 rounded-lg hover:bg-n-alpha-1"
        @click="emit('cancel')"
      >
        {{ t('TASKS.FORM.CANCEL') }}
      </button>
      <button
        class="px-3 py-1.5 text-sm bg-n-brand text-white rounded-lg hover:brightness-110 disabled:opacity-50"
        :disabled="isSubmitting || !title.trim()"
        @click="submit"
      >
        {{ t('TASKS.FORM.SAVE') }}
      </button>
    </div>
  </div>
</template>
